import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import {
  LogOut, Plus, Trash2, Pencil, Lock, Save, KeyRound, Upload, ArrowUp, ArrowDown,
  Eye, EyeOff, Users, ExternalLink, Menu as MenuIcon, ChevronsLeft, Home as HomeIcon,
  BedDouble, UtensilsCrossed, Images, Sparkles, Award, MapPin, Sun, Settings as SettingsIcon,
  Briefcase, ClipboardList, ShieldCheck, ArrowLeft, Activity, Star,
} from "lucide-react";
import { api, endpoints, asset } from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import { toast } from "sonner";
import {
  getToken, saveSession, clearSession, touchActivity, isExpiredByInactivity,
  blockBackNavigation, INACTIVITY_MS,
} from "../lib/adminSession";

const SCHEMA = {
  rooms: ["name", "slug", "tagline", "capacity", "beds", "image", "description", "amenities", "order"],
  menu: ["name", "category", "spice", "price", "image", "description", "order"],
  gallery: ["title", "image", "span", "order"],
  offers: ["title", "badge", "description", "order"],
  facilities: ["name", "icon", "image", "description", "order"],
  awards: ["title", "image", "order"],
  attractions: ["name", "category", "image", "description", "distance", "travel_time", "address", "directions_url", "website_url", "order"],
  certifications: ["title", "description", "image", "file_url", "expires_on", "order"],
  reviews: ["guest_name", "rating", "text", "review_date", "source", "source_url", "order"],
};
const IMAGE_FIELDS = ["image"];
const inp = "w-full bg-[#F7F5F0] border border-[#0B192C]/15 rounded-lg px-3 py-2 text-[#222] text-sm outline-none focus:border-[#D4AF37]";

// Human-friendly labels & help text for each schema key (kept short & professional).
const FIELD_LABEL = {
  guest_name: "Guest name", rating: "Star rating", text: "Review text", review_date: "Review date",
  source: "Review source", source_url: "Link to original review", order: "Display order",
  name: "Name", slug: "URL slug", tagline: "Short tagline", capacity: "Sleeps (number of guests)",
  beds: "Bed configuration", amenities: "Amenities (comma-separated)", description: "Description",
  category: "Category", spice: "Spice level", price: "Price", image: "Image",
  title: "Title", badge: "Badge", icon: "Icon (lucide name)",
  distance: "Distance", travel_time: "Travel time", address: "Address",
  directions_url: "Google Maps directions URL", website_url: "Website URL",
  file_url: "Document link", expires_on: "Expiry date", span: "Grid span (1–2)",
};
const FIELD_HELP = {
  slug: "Lowercase, no spaces (e.g. queen-deluxe)", rating: "Tap the stars to set 1–5",
  amenities: "Separate each amenity with a comma (Wi-Fi, TV, Kettle)",
  source_url: "Full link to the review page (https://…)",
};
// Fixed option lists — kept identical to what the public site already uses.
const OPTIONS = {
  "menu.category": ["Entrée", "Main Meals", "Biryani", "Curries", "Sides", "Desserts", "Beverages"],
  "menu.spice": ["", "Mild", "Medium", "Hot"],
  "reviews.source": ["Google", "Booking.com", "Direct", "Facebook", "TripAdvisor", "Other"],
  "attractions.category": ["Landmark", "Museum", "Nature", "Food", "Family", "Other"],
  "gallery.span": ["normal", "tall", "wide"],
};
const NUMBER_FIELDS = new Set(["order"]);
const DATE_FIELDS = new Set(["expires_on", "review_date"]);
const URL_FIELDS = new Set(["directions_url", "website_url", "source_url", "file_url"]);
const TEXTAREA_FIELDS = new Set(["description", "text"]);

// Interactive 1–5 star picker (click a star, or hover to preview).
function StarPicker({ value, onChange }) {
  const [hover, setHover] = useState(0);
  const n = Math.max(0, Math.min(5, Number(value) || 0));
  const view = hover || n;
  return (
    <div className="flex items-center gap-2" role="radiogroup" aria-label="Star rating" data-testid="star-picker">
      <div className="flex items-center gap-1">
        {[1, 2, 3, 4, 5].map((s) => (
          <button key={s} type="button" onMouseEnter={() => setHover(s)} onMouseLeave={() => setHover(0)}
            onClick={() => onChange(s)} aria-label={`${s} star${s === 1 ? "" : "s"}`} role="radio" aria-checked={n === s}
            data-testid={`star-${s}`}
            className="p-1 rounded transition-transform hover:scale-110 focus:outline-none focus:ring-2 focus:ring-[#D4AF37]/40">
            <Star size={22} className={s <= view ? "text-[#D4AF37]" : "text-[#0B192C]/25"} fill={s <= view ? "#D4AF37" : "transparent"} strokeWidth={1.6} />
          </button>
        ))}
      </div>
      <span className="text-sm text-[#0B192C]/70 font-medium tabular-nums">{n ? `${n} / 5` : "Not rated"}</span>
      {n > 0 && (
        <button type="button" onClick={() => onChange(0)} className="ml-1 text-[11px] text-[#0B192C]/50 hover:text-red-500" data-testid="star-clear">clear</button>
      )}
    </div>
  );
}

// ------------------------------ noindex helper ------------------------------
function useNoIndex() {
  useEffect(() => {
    const meta = document.createElement("meta");
    meta.name = "robots";
    meta.content = "noindex, nofollow, noarchive";
    document.head.appendChild(meta);
    const prevTitle = document.title;
    document.title = "Byer Fountain · Secure Admin";
    return () => { document.head.removeChild(meta); document.title = prevTitle; };
  }, []);
}

// ------------------------------ Login page ---------------------------------
function Login({ onDone, message }) {
  useNoIndex();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [remember, setRemember] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await endpoints.login({ email, password, remember_me: remember });
      saveSession(r.token, remember);
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Invalid email or password");
    } finally { setBusy(false); }
  };
  const forgot = async () => {
    if (!email) { toast.error("Enter your email above first."); return; }
    try { await api.post("/auth/forgot-password", { email }); toast.success("If that account exists, a reset link has been emailed."); }
    catch { toast.error("Could not send reset email."); }
  };

  return (
    <div className="min-h-screen relative overflow-hidden bg-[#0B192C] grain" data-testid="admin-login">
      <div className="absolute inset-0 opacity-40 pointer-events-none"
        style={{ background: "radial-gradient(1200px 600px at 20% 10%, #D4AF3733, transparent 60%), radial-gradient(900px 500px at 80% 90%, #D4AF3722, transparent 60%)" }} />
      <div className="relative z-10 min-h-screen grid place-items-center px-5 py-16">
        <form onSubmit={submit} className="w-full max-w-md bg-white/95 backdrop-blur rounded-3xl p-9 md:p-10 shadow-2xl border border-[#D4AF37]/20">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-full bg-[#0B192C] grid place-items-center"><ShieldCheck className="text-[#D4AF37]" size={22} /></div>
            <div>
              <p className="text-[10px] tracking-[0.28em] text-[#D4AF37] uppercase">Byer Fountain Motor Inn</p>
              <h1 className="font-serif text-2xl text-[#0B192C] leading-tight">Secure Staff Portal</h1>
            </div>
          </div>
          {message && (
            <div className="mb-5 text-sm p-3 rounded-lg bg-[#D4AF37]/15 border border-[#D4AF37]/40 text-[#0B192C]">{message}</div>
          )}
          <label className="text-xs uppercase tracking-wider text-[#0B192C]/60">Email</label>
          <input className={inp + " mb-3 py-3"} placeholder="you@byerfountainmotorinn.com.au" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="login-email" autoComplete="username" />
          <label className="text-xs uppercase tracking-wider text-[#0B192C]/60">Password</label>
          <div className="relative mb-2">
            <input className={inp + " py-3 pr-11"} type={showPw ? "text" : "password"} placeholder="••••••••" value={password} onChange={(e) => setPassword(e.target.value)} data-testid="login-password" autoComplete="current-password" />
            <button type="button" onClick={() => setShowPw((v) => !v)} aria-label={showPw ? "Hide password" : "Show password"} data-testid="login-password-toggle"
              className="absolute right-3 top-1/2 -translate-y-1/2 w-7 h-7 grid place-items-center rounded-md text-[#0B192C]/50 hover:text-[#0B192C] hover:bg-[#0B192C]/5 transition-colors">
              {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
            </button>
          </div>
          <div className="flex items-center justify-between mt-3 mb-5">
            <label className="flex items-center gap-2 text-sm text-[#0B192C] cursor-pointer select-none">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} className="accent-[#0B192C] w-4 h-4" data-testid="login-remember" />
              Remember me
            </label>
            <button type="button" onClick={forgot} className="text-xs text-[#0B192C]/70 hover:text-[#D4AF37]" data-testid="login-forgot">Forgot password?</button>
          </div>
          <button disabled={busy} className="w-full bg-[#0B192C] text-white py-3.5 rounded-xl font-medium hover:bg-[#D4AF37] hover:text-[#0B192C] transition-colors" data-testid="login-submit">
            {busy ? "Signing in…" : "Sign In"}
          </button>
          <div className="mt-6 text-center">
            <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#0B192C]/70 hover:text-[#D4AF37]" data-testid="login-return-home">
              <ArrowLeft size={14} /> Return to main website
            </Link>
          </div>
          <p className="mt-6 text-[11px] text-[#0B192C]/45 text-center">
            Authorised personnel only. Sessions automatically end after 30 minutes of inactivity.
          </p>
        </form>
      </div>
    </div>
  );
}

// ------------------------------ Reset Password -----------------------------
function ResetPassword({ token, onDone }) {
  useNoIndex();
  const [pw, setPw] = useState(""); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (pw.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setBusy(true);
    try { await api.post("/auth/reset-password", { token, new_password: pw }); toast.success("Password reset. Please sign in."); onDone(); }
    catch (err) { toast.error(err.response?.data?.detail || "Reset link invalid or expired."); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen grid place-items-center bg-[#0B192C] px-5" data-testid="reset-password">
      <form onSubmit={submit} className="w-full max-w-sm bg-white rounded-2xl p-8 shadow-2xl">
        <div className="w-12 h-12 rounded-full bg-[#0B192C] grid place-items-center mb-5"><KeyRound className="text-[#D4AF37]" size={20} /></div>
        <h1 className="font-serif text-3xl text-[#0B192C] mb-5">Set a new password</h1>
        <input className={inp + " mb-4 py-3"} type="password" placeholder="New password (min 8 chars)" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="reset-new" />
        <button disabled={busy} className="w-full bg-[#0B192C] text-white py-3.5 rounded-xl font-medium hover:bg-[#D4AF37] hover:text-[#0B192C]" data-testid="reset-submit">
          {busy ? "Saving…" : "Reset Password"}
        </button>
      </form>
    </div>
  );
}

// ------------------------------ Image Field --------------------------------
function ImageField({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", file);
    try { const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); onChange(r.data.url); toast.success("Image uploaded"); }
    catch { toast.error("Upload failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="space-y-2" data-testid="image-field">
      {value && <img src={asset(value)} alt="preview" className="w-full h-32 object-cover rounded-lg border border-[#0B192C]/10" />}
      <label className="flex items-center justify-center gap-2 cursor-pointer bg-[#0B192C] text-white text-sm py-2.5 rounded-lg hover:bg-[#D4AF37] hover:text-[#0B192C] transition-colors" data-testid="upload-from-device">
        <Upload size={15} /> {busy ? "Uploading…" : "Upload Image From Device"}
        <input type="file" accept="image/*" className="hidden" onChange={(e) => upload(e.target.files[0])} data-testid="image-upload-input" />
      </label>
      <input className={inp} placeholder="…or paste an image URL (optional)" value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid="image-url-input" />
    </div>
  );
}

// ------------------------------ Edit Modal ---------------------------------
function EditModal({ collection, item, onClose, onSaved }) {
  const [data, setData] = useState(() => {
    const base = {};
    SCHEMA[collection].forEach((f) => { base[f] = item?.[f] ?? (f === "order" ? 0 : ""); });
    if (Array.isArray(item?.amenities)) base.amenities = item.amenities.join(", ");
    return base;
  });
  const save = async () => {
    const payload = { ...data };
    if (collection === "rooms" && typeof payload.amenities === "string")
      payload.amenities = payload.amenities.split(",").map((s) => s.trim()).filter(Boolean);
    payload.order = Number(payload.order) || 0;
    if (collection === "reviews") payload.rating = Number(payload.rating) || 0;
    if (collection === "gallery" && !payload.span) payload.span = "normal";
    try {
      if (item?.id) await api.put(`/admin/${collection}/${item.id}`, payload);
      else await api.post(`/admin/${collection}`, payload);
      toast.success("Saved"); onSaved();
    } catch { toast.error("Save failed"); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">{item?.id ? "Edit" : "New"} {collection.slice(0, -1)}</DialogTitle>
          <DialogDescription>Fill the fields below. Interactive stars, dropdowns and date pickers help you avoid typos.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3.5 mt-2">
          {SCHEMA[collection].map((f) => {
            const key = `${collection}.${f}`;
            const label = FIELD_LABEL[f] || f.replace(/_/g, " ");
            const help = FIELD_HELP[f];
            let control;
            if (IMAGE_FIELDS.includes(f)) {
              control = <ImageField value={data[f]} onChange={(v) => setData({ ...data, [f]: v })} />;
            } else if (f === "rating") {
              control = <StarPicker value={data[f]} onChange={(v) => setData({ ...data, [f]: v })} />;
            } else if (OPTIONS[key]) {
              control = (
                <select className={inp + " appearance-none pr-9 bg-[url('data:image/svg+xml;utf8,<svg xmlns=%22http://www.w3.org/2000/svg%22 width=%2212%22 height=%2212%22 viewBox=%220 0 12 12%22><path d=%22M2 4l4 4 4-4%22 stroke=%22%230B192C%22 stroke-width=%221.5%22 fill=%22none%22 stroke-linecap=%22round%22 stroke-linejoin=%22round%22/></svg>')] bg-no-repeat bg-[right_0.75rem_center] cursor-pointer"}
                        value={data[f] || ""} onChange={(e) => setData({ ...data, [f]: e.target.value })} data-testid={`field-${f}`}>
                  {OPTIONS[key].map((opt) => <option key={opt || "_"} value={opt}>{opt || "— None —"}</option>)}
                </select>
              );
            } else if (TEXTAREA_FIELDS.has(f)) {
              control = <textarea rows={4} className={`${inp} resize-y min-h-[96px]`} value={data[f] || ""} onChange={(e) => setData({ ...data, [f]: e.target.value })} data-testid={`field-${f}`} />;
            } else if (DATE_FIELDS.has(f)) {
              control = <input type="date" className={inp} value={data[f] || ""} onChange={(e) => setData({ ...data, [f]: e.target.value })} data-testid={`field-${f}`} />;
            } else if (URL_FIELDS.has(f)) {
              control = <input type="url" placeholder="https://…" className={inp} value={data[f] || ""} onChange={(e) => setData({ ...data, [f]: e.target.value })} data-testid={`field-${f}`} />;
            } else if (NUMBER_FIELDS.has(f)) {
              control = <input type="number" min={0} className={inp} value={data[f] ?? 0} onChange={(e) => setData({ ...data, [f]: e.target.value === "" ? "" : Number(e.target.value) })} data-testid={`field-${f}`} />;
            } else {
              control = <input className={inp} value={data[f] || ""} onChange={(e) => setData({ ...data, [f]: e.target.value })} data-testid={`field-${f}`} />;
            }
            return (
              <div key={f}>
                <label className="text-xs uppercase tracking-wide text-[#0B192C]/60 mb-1 block font-medium">{label}</label>
                {control}
                {help && <p className="text-[11px] text-[#0B192C]/50 mt-1">{help}</p>}
              </div>
            );
          })}
          <button onClick={save} className="w-full bg-[#0B192C] text-white py-3 rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-[#D4AF37] hover:text-[#0B192C] transition-colors sticky bottom-0" data-testid="save-item">
            <Save size={16} /> Save
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------ Manager ------------------------------------
function Manager({ collection, onActivity }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => api.get(`/admin/${collection}`).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, [collection]);
  const del = async (id) => {
    if (!window.confirm("Are you sure you want to permanently delete this item? This cannot be undone.")) return;
    await api.delete(`/admin/${collection}/${id}`); toast.success("Deleted"); load();
    onActivity?.(`Deleted item from ${collection}`);
  };
  const togglePub = async (it) => {
    await api.put(`/admin/${collection}/${it.id}`, { published: it.published === false });
    load();
    onActivity?.(`${it.published === false ? "Published" : "Hid"} “${it.name || it.title || it.guest_name || it.author || "item"}” in ${collection}`);
  };
  const move = async (idx, dir) => {
    const j = idx + dir; if (j < 0 || j >= items.length) return;
    const a = items[idx], b = items[j];
    await api.put(`/admin/${collection}/${a.id}`, { order: b.order });
    await api.put(`/admin/${collection}/${b.id}`, { order: a.order });
    load();
  };
  const title = (i) => i.name || i.title || i.guest_name || i.author || "(untitled)";
  return (
    <div>
      <div className="flex justify-between items-center mb-5">
        <p className="text-[#222]/60 text-sm">{items.length} items · drafts hidden from public site</p>
        <button onClick={() => setEditing({})} className="flex items-center gap-2 bg-[#0B192C] text-white px-4 py-2 rounded-full text-sm hover:bg-[#D4AF37] hover:text-[#0B192C] transition-colors" data-testid="add-item"><Plus size={16} /> Add</button>
      </div>
      <div className="space-y-3">
        {items.map((it, idx) => (
          <div key={it.id} className={`flex gap-3 items-center bg-white rounded-xl p-3 shadow-sm ${it.published === false ? "opacity-60" : ""}`} data-testid={`admin-item-${it.id}`}>
            <div className="flex flex-col">
              <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-[#0B192C]/50 hover:text-[#D4AF37] disabled:opacity-20" data-testid={`up-${it.id}`}><ArrowUp size={14} /></button>
              <button onClick={() => move(idx, 1)} disabled={idx === items.length - 1} className="text-[#0B192C]/50 hover:text-[#D4AF37] disabled:opacity-20" data-testid={`down-${it.id}`}><ArrowDown size={14} /></button>
            </div>
            {it.image && <img src={asset(it.image)} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />}
            <div className="flex-1 min-w-0">
              <p className="font-serif text-lg text-[#0B192C] truncate">{title(it)}</p>
              <p className="text-xs text-[#222]/50 truncate">
                {it.category || it.tagline || it.badge || it.distance || it.expires_on || ""}
                {it.published === false ? " · DRAFT" : ""}
              </p>
            </div>
            <button onClick={() => togglePub(it)} title={it.published === false ? "Publish" : "Hide"} className="p-2 text-[#0B192C] hover:text-[#D4AF37]" data-testid={`toggle-${it.id}`}>{it.published === false ? <EyeOff size={16} /> : <Eye size={16} />}</button>
            <button onClick={() => setEditing(it)} className="p-2 text-[#0B192C] hover:text-[#D4AF37]" data-testid={`edit-${it.id}`}><Pencil size={16} /></button>
            <button onClick={() => del(it.id)} className="p-2 text-red-500 hover:text-red-700" data-testid={`delete-${it.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      {editing && <EditModal collection={collection} item={editing.id ? editing : null} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); onActivity?.(`Saved item in ${collection}`); }} />}
    </div>
  );
}

// ------------------------------ Leads / Settings / Staff -------------------
function Leads({ collection, label }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get(`/admin/${collection}`).then((r) => setItems(r.data)).catch(() => {}); }, [collection]);
  return (
    <div className="space-y-3" data-testid={`leads-${collection}`}>
      {items.length === 0 && <p className="text-[#222]/50">No {label} yet.</p>}
      {items.map((it) => (
        <div key={it.id} className="bg-white rounded-xl p-5 shadow-sm">
          <div className="flex justify-between flex-wrap gap-2">
            <p className="font-serif text-xl text-[#0B192C]">{it.company || it.guest_name}</p>
            <span className="text-xs bg-[#D4AF37]/20 text-[#0B192C] px-2 py-1 rounded-full h-fit">{new Date(it.created_at).toLocaleDateString()}</span>
          </div>
          <p className="text-sm text-[#222]/70 mt-1">{it.contact_name}{it.email ? ` · ${it.email}` : ""}{it.phone ? ` · ${it.phone}` : ""}{it.room_or_phone ? ` · ${it.room_or_phone}` : ""}</p>
          {it.items && <p className="text-sm text-[#0B192C] mt-2">Dishes: {it.items.join(", ")}{it.total ? ` · ${it.total}` : ""}</p>}
          {(it.duration || it.guests || it.industry) && <p className="text-sm text-[#222]/60 mt-1">{[it.industry, it.guests, it.duration].filter(Boolean).join(" · ")}</p>}
          {(it.message || it.notes || it.when) && <p className="text-sm text-[#222]/60 mt-2 italic">{it.message || `${it.when ? "Time: " + it.when + ". " : ""}${it.notes || ""}`}</p>}
        </div>
      ))}
    </div>
  );
}

const SETTINGS_FIELDS = [["phone", "Phone"], ["email", "Email"], ["address", "Address"], ["checkin", "Check-in time"], ["checkout", "Check-out time"], ["reception_weekday", "Reception Mon–Fri"], ["reception_weekend", "Reception Sat–Sun"], ["announcement", "Announcement / notice"], ["covid_safe_text", "COVID-Safe practices text"]];
function Settings() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/site-settings").then((r) => setS(r.data)).catch(() => {}); }, []);
  const save = async () => { try { await api.put("/admin/site-settings", s); toast.success("Settings saved"); } catch { toast.error("Save failed"); } };
  if (!s) return <p className="text-[#222]/50">Loading…</p>;
  return (
    <div className="max-w-lg space-y-3" data-testid="settings-form">
      {SETTINGS_FIELDS.map(([k, l]) => (
        <div key={k}>
          <label className="text-xs uppercase tracking-wide text-[#0B192C]/50 mb-1 block">{l}</label>
          {k === "covid_safe_text" || k === "announcement" ?
            <textarea rows={3} className={`${inp} resize-none`} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`setting-${k}`} /> :
            <input className={inp} value={s[k] || ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} data-testid={`setting-${k}`} />}
        </div>
      ))}
      <button onClick={save} className="bg-[#0B192C] text-white px-6 py-2.5 rounded-lg font-medium hover:bg-[#D4AF37] hover:text-[#0B192C]" data-testid="settings-save">Save Settings</button>
    </div>
  );
}

function Staff() {
  const [list, setList] = useState([]);
  const [form, setForm] = useState({ email: "", password: "", name: "", role: "staff" });
  const [err, setErr] = useState("");
  const load = () => api.get("/admin/staff").then((r) => { setList(r.data); setErr(""); }).catch((e) => setErr(e.response?.data?.detail || "Only administrators can manage staff."));
  useEffect(() => { load(); }, []);
  const add = async () => {
    try { await api.post("/admin/staff", form); toast.success("Staff added"); setForm({ email: "", password: "", name: "", role: "staff" }); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not add staff"); }
  };
  const del = async (id) => { if (!window.confirm("Remove this staff account?")) return; await api.delete(`/admin/staff/${id}`); load(); };
  if (err) return <p className="text-[#222]/60" data-testid="staff-denied">{err}</p>;
  return (
    <div className="max-w-lg space-y-4" data-testid="staff-manager">
      <div className="space-y-2">
        {list.map((u) => (
          <div key={u.id} className="flex items-center justify-between bg-white rounded-xl p-3 shadow-sm">
            <div><p className="text-[#0B192C] font-medium text-sm">{u.name} <span className="text-xs text-[#D4AF37] uppercase">{u.role}</span></p><p className="text-xs text-[#222]/50">{u.email}</p></div>
            <button onClick={() => del(u.id)} className="p-2 text-red-500 hover:text-red-700"><Trash2 size={16} /></button>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-xl p-4 shadow-sm space-y-2">
        <p className="font-serif text-lg text-[#0B192C]">Add staff account</p>
        <input className={inp} placeholder="Name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" />
        <input className={inp} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" />
        <input className={inp} type="password" placeholder="Password (min 8)" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" />
        <select className={inp} value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="staff-role">
          <option value="staff">Staff</option><option value="admin">Administrator</option>
        </select>
        <button onClick={add} className="bg-[#0B192C] text-white px-5 py-2.5 rounded-lg font-medium hover:bg-[#D4AF37] hover:text-[#0B192C]" data-testid="staff-add">Add Staff</button>
      </div>
    </div>
  );
}

function ChangePassword({ onClose }) {
  const [cur, setCur] = useState(""); const [nw, setNw] = useState(""); const [confirm, setConfirm] = useState(""); const [busy, setBusy] = useState(false);
  const save = async () => {
    if (nw.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (nw !== confirm) { toast.error("New passwords do not match."); return; }
    setBusy(true);
    try { const r = await api.post("/auth/change-password", { current_password: cur, new_password: nw }); toast.success(r.data.message || "Password updated."); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not update password."); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        <DialogHeader><DialogTitle className="font-serif text-2xl flex items-center gap-2"><KeyRound size={18} className="text-[#D4AF37]" /> Change Password</DialogTitle>
          <DialogDescription>Update the password for this staff account. Minimum 8 characters.</DialogDescription></DialogHeader>
        <div className="space-y-3 mt-2">
          <input type="password" className={inp} placeholder="Current password" value={cur} onChange={(e) => setCur(e.target.value)} data-testid="cp-current" />
          <input type="password" className={inp} placeholder="New password (min 8 chars)" value={nw} onChange={(e) => setNw(e.target.value)} data-testid="cp-new" />
          <input type="password" className={inp} placeholder="Confirm new password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="cp-confirm" />
          <button onClick={save} disabled={busy} className="w-full bg-[#0B192C] text-white py-3 rounded-lg font-medium hover:bg-[#D4AF37] hover:text-[#0B192C] transition-colors disabled:opacity-60" data-testid="cp-save">{busy ? "Updating…" : "Update Password"}</button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ------------------------------ Dashboard Home -----------------------------
function DashboardHome({ activity, onNav, isAdmin }) {
  const [counts, setCounts] = useState({});
  // Which collections each role is allowed to see counts / shortcuts for.
  const adminKeys = ["rooms", "menu", "gallery", "offers", "facilities", "awards", "attractions", "certifications", "corporate-enquiries", "pre-orders"];
  const staffKeys = ["menu", "corporate-enquiries", "pre-orders"];
  const keys = isAdmin ? adminKeys : staffKeys;
  useEffect(() => {
    (async () => {
      const results = await Promise.all(
        keys.map(async (k) => {
          try { const r = await api.get(`/admin/${k}`); return [k, r.data.length]; } catch { return [k, "—"]; }
        })
      );
      setCounts(Object.fromEntries(results));
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const adminStats = [
    ["Rooms", "rooms", BedDouble],
    ["Menu items", "menu", UtensilsCrossed],
    ["Gallery photos", "gallery", Images],
    ["Attractions", "attractions", MapPin],
    ["Facilities", "facilities", Sparkles],
    ["Awards", "awards", Award],
    ["Corporate leads", "corporate-enquiries", Briefcase],
    ["Pre-orders", "pre-orders", ClipboardList],
  ];
  const staffStats = [
    ["Menu items", "menu", UtensilsCrossed],
    ["Corporate leads", "corporate-enquiries", Briefcase],
    ["Pre-orders", "pre-orders", ClipboardList],
  ];
  const stats = isAdmin ? adminStats : staffStats;

  const adminShortcuts = [
    { k: "attractions", label: "Manage Visit Holbrook", icon: MapPin },
    { k: "gallery", label: "Image Library", icon: Images },
    { k: "rooms", label: "Manage Rooms", icon: BedDouble },
    { k: "menu", label: "Manage Menu", icon: UtensilsCrossed },
    { k: "certifications", label: "Certifications", icon: ShieldCheck },
    { k: "settings", label: "Site Settings", icon: SettingsIcon },
  ];
  const staffShortcuts = [
    { k: "menu", label: "Manage Menu", icon: UtensilsCrossed },
    { k: "orders", label: "Pre-orders", icon: ClipboardList },
    { k: "corporate", label: "Corporate Leads", icon: Briefcase },
    { k: "reviews", label: "Guest Reviews", icon: Star },
  ];
  const shortcuts = isAdmin ? adminShortcuts : staffShortcuts;

  return (
    <div className="space-y-8" data-testid="dashboard-home">
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map(([label, key, Icon]) => (
          <div key={label} className="bg-white rounded-2xl p-5 shadow-sm border border-[#0B192C]/5">
            <div className="flex items-center justify-between">
              <p className="text-xs uppercase tracking-widest text-[#0B192C]/50">{label}</p>
              <Icon size={18} className="text-[#D4AF37]" />
            </div>
            <p className="font-serif text-4xl text-[#0B192C] mt-3">{counts[key] ?? "—"}</p>
          </div>
        ))}
      </div>

      <div>
        <p className="text-xs uppercase tracking-widest text-[#0B192C]/60 mb-3">Shortcuts</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {shortcuts.map((s) => (
            <button key={s.k} onClick={() => onNav(s.k)} className="text-left bg-white rounded-xl p-4 shadow-sm border border-[#0B192C]/5 hover:border-[#D4AF37]/50 hover:shadow-md transition-all flex items-center gap-3" data-testid={`shortcut-${s.k}`}>
              <div className="w-10 h-10 rounded-full bg-[#0B192C] grid place-items-center"><s.icon size={16} className="text-[#D4AF37]" /></div>
              <div>
                <p className="font-serif text-lg text-[#0B192C] leading-tight">{s.label}</p>
                <p className="text-xs text-[#222]/50">Open</p>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <p className="text-xs uppercase tracking-widest text-[#0B192C]/60 mb-3 flex items-center gap-2"><Activity size={13} /> Recent activity</p>
        <div className="bg-white rounded-2xl p-5 shadow-sm border border-[#0B192C]/5 min-h-[120px]">
          {activity.length === 0 ? (
            <p className="text-[#222]/50 text-sm">No activity yet this session — start managing content above.</p>
          ) : (
            <ul className="space-y-2">
              {activity.slice(0, 8).map((a, i) => (
                <li key={i} className="text-sm text-[#0B192C] flex justify-between gap-4 border-b border-[#0B192C]/5 pb-2 last:border-b-0">
                  <span>{a.text}</span>
                  <span className="text-xs text-[#222]/50 shrink-0">{a.time}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}

// ------------------------------ Sidebar ------------------------------------
const SECTIONS = [
  { k: "home", label: "Dashboard", icon: HomeIcon },
  { k: "rooms", label: "Rooms", icon: BedDouble },
  { k: "menu", label: "Menu", icon: UtensilsCrossed },
  { k: "gallery", label: "Image Library", icon: Images },
  { k: "facilities", label: "Facilities", icon: Sparkles },
  { k: "awards", label: "Awards", icon: Award },
  { k: "certifications", label: "Certifications", icon: ShieldCheck },
  { k: "attractions", label: "Visit Holbrook", icon: MapPin },
  { k: "reviews", label: "Guest Reviews", icon: Star },
  { k: "offers", label: "Offers", icon: Sun },
  { k: "corporate", label: "Corporate Leads", icon: Briefcase },
  { k: "orders", label: "Pre-orders", icon: ClipboardList },
  { k: "settings", label: "Settings", icon: SettingsIcon },
  { k: "staff", label: "Staff", icon: Users },
];

function Sidebar({ sections, roleLabel, current, onNav, collapsed, setCollapsed, mobileOpen, setMobileOpen }) {
  const item = (s) => (
    <button
      key={s.k}
      onClick={() => { onNav(s.k); setMobileOpen(false); }}
      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${current === s.k ? "bg-[#D4AF37] text-[#0B192C] font-medium" : "text-white/80 hover:bg-white/5 hover:text-white"}`}
      data-testid={`nav-${s.k}`}
      title={collapsed ? s.label : ""}
    >
      <s.icon size={17} className="shrink-0" />
      {!collapsed && <span className="truncate">{s.label}</span>}
    </button>
  );
  const nav = (
    <nav className={`h-full bg-[#0B192C] border-r border-white/5 flex flex-col ${collapsed ? "w-16" : "w-64"} transition-all`}>
      <div className={`px-4 py-5 flex items-center ${collapsed ? "justify-center" : "justify-between"} border-b border-white/5`}>
        {!collapsed && (
          <div>
            <p className="text-[10px] tracking-[0.28em] text-[#D4AF37] uppercase">Byer Fountain</p>
            <p className="font-serif text-white text-lg leading-tight" data-testid="sidebar-role-label">{roleLabel}</p>
          </div>
        )}
        <button onClick={() => setCollapsed(!collapsed)} className="text-white/60 hover:text-[#D4AF37]" data-testid="sidebar-collapse" title="Collapse sidebar">
          <ChevronsLeft size={18} className={`transition-transform ${collapsed ? "rotate-180" : ""}`} />
        </button>
      </div>
      <div className="flex-1 overflow-y-auto p-3 space-y-1">
        {sections.map(item)}
      </div>
    </nav>
  );
  return (
    <>
      {/* desktop */}
      <div className="hidden md:block sticky top-0 h-screen">{nav}</div>
      {/* mobile */}
      {mobileOpen && (
        <div className="md:hidden fixed inset-0 z-40" data-testid="admin-sidebar-mobile">
          <div className="absolute inset-0 bg-black/60" onClick={() => setMobileOpen(false)} />
          <div className="absolute inset-y-0 left-0 w-64">{nav}</div>
        </div>
      )}
    </>
  );
}

// ------------------------------ Main Admin ---------------------------------
export default function Admin() {
  useNoIndex();
  const [authed, setAuthed] = useState(false);
  const [me, setMe] = useState(null); // { email, name, role }
  const [checking, setChecking] = useState(true);
  const [changingPw, setChangingPw] = useState(false);
  const [current, setCurrent] = useState("home");
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activity, setActivity] = useState([]);
  const [loginMsg, setLoginMsg] = useState("");
  const resetToken = new URLSearchParams(window.location.search).get("reset");
  const inactivityTimer = useRef(null);
  const isAdmin = me?.role === "admin";
  // Sections shown to non-admin (staff): day-to-day website content only.
  // Structural / security sections stay admin-only.
  const STAFF_ALLOWED = new Set(["home", "menu", "reviews", "orders", "corporate"]);
  const visibleSections = isAdmin ? SECTIONS : SECTIONS.filter((s) => STAFF_ALLOWED.has(s.k));

  const pushActivity = useCallback((text) => {
    setActivity((prev) => [{ text, time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) }, ...prev].slice(0, 20));
  }, []);

  const doLogout = useCallback((reason = "") => {
    clearSession();
    setAuthed(false);
    setMe(null);
    setLoginMsg(reason);
    // Clear URL and prevent going back into the dashboard via history
    try { window.history.replaceState({}, "", "/secure-admin"); } catch (_) { /* ignore */ }
  }, []);

  // 30-min inactivity watcher
  useEffect(() => {
    if (!authed) return;
    const reset = () => {
      touchActivity();
      clearTimeout(inactivityTimer.current);
      inactivityTimer.current = setTimeout(() => {
        doLogout("You were signed out after 30 minutes of inactivity.");
      }, INACTIVITY_MS);
    };
    const events = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    reset();
    // Check every 60s in case timers fell asleep (e.g. tab was backgrounded)
    const interval = setInterval(() => {
      if (isExpiredByInactivity()) doLogout("You were signed out after 30 minutes of inactivity.");
    }, 60_000);
    return () => {
      events.forEach((e) => window.removeEventListener(e, reset));
      clearTimeout(inactivityTimer.current);
      clearInterval(interval);
    };
  }, [authed, doLogout]);

  // Initial auth check
  useEffect(() => {
    if (resetToken) { setChecking(false); return; }
    const t = getToken();
    if (!t) { setChecking(false); return; }
    if (isExpiredByInactivity()) {
      clearSession(); setChecking(false); setLoginMsg("Session expired. Please sign in again.");
      return;
    }
    api.get("/auth/me").then((r) => { setMe(r.data); setAuthed(true); }).catch(() => clearSession()).finally(() => setChecking(false));
  }, [resetToken]);

  const viewLiveWebsite = () => {
    clearSession();
    blockBackNavigation("/?admin_signed_out=1");
    window.location.replace("/?admin_signed_out=1");
  };

  const signOut = () => {
    clearSession();
    blockBackNavigation("/secure-admin?signed_out=1");
    window.location.replace("/secure-admin?signed_out=1");
  };

  if (resetToken && !authed)
    return <ResetPassword token={resetToken} onDone={() => { window.history.replaceState({}, "", "/secure-admin"); }} />;
  if (checking)
    return <div className="min-h-screen grid place-items-center bg-[#0B192C] text-white/60">Loading…</div>;
  if (!authed) {
    const signedOut = new URLSearchParams(window.location.search).get("signed_out") === "1";
    return <Login onDone={() => { api.get("/auth/me").then((r) => setMe(r.data)).catch(() => {}); setAuthed(true); }} message={loginMsg || (signedOut ? "You have been signed out." : "")} />;
  }

  const currentLabel = (visibleSections.find((s) => s.k === current) || SECTIONS.find((s) => s.k === current) || {}).label || "Dashboard";
  // If a staff session tries to view a section that's now hidden (e.g. after a role change), send them home.
  if (me && !isAdmin && !STAFF_ALLOWED.has(current)) {
    setTimeout(() => setCurrent("home"), 0);
  }

  return (
    <div className="min-h-screen bg-[#F7F5F0] flex" data-testid="admin-dashboard">
      <Sidebar sections={visibleSections} roleLabel={isAdmin ? "Admin" : "Staff"} current={current} onNav={setCurrent} collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />

      <div className="flex-1 min-w-0 flex flex-col">
        {/* Top bar */}
        <header className="bg-white border-b border-[#0B192C]/10 px-5 md:px-8 py-4 flex justify-between items-center sticky top-0 z-20">
          <div className="flex items-center gap-3 min-w-0">
            <button className="md:hidden text-[#0B192C]" onClick={() => setMobileOpen(true)} data-testid="admin-menu-open"><MenuIcon size={22} /></button>
            <div className="min-w-0">
              <p className="text-[10px] tracking-[0.24em] text-[#D4AF37] uppercase">Byer Fountain Motor Inn</p>
              <h1 className="font-serif text-xl md:text-2xl text-[#0B192C] leading-tight truncate" data-testid="admin-page-title">{currentLabel}</h1>
            </div>
          </div>
          <div className="flex items-center gap-2 md:gap-4">
            <button onClick={viewLiveWebsite} title="Securely end session and view public site" className="hidden sm:flex items-center gap-2 text-sm bg-[#0B192C] text-white px-3 py-2 rounded-full hover:bg-[#D4AF37] hover:text-[#0B192C] transition-colors" data-testid="admin-view-live">
              <ExternalLink size={14} /> View Live Website
            </button>
            <button onClick={() => setChangingPw(true)} className="flex items-center gap-2 text-sm text-[#0B192C]/70 hover:text-[#D4AF37]" data-testid="admin-change-password"><KeyRound size={16} /> <span className="hidden md:inline">Change password</span></button>
            <button onClick={signOut} className="flex items-center gap-2 text-sm text-[#0B192C]/70 hover:text-red-600" data-testid="admin-logout"><LogOut size={16} /> <span className="hidden md:inline">Sign out</span></button>
          </div>
        </header>

        {changingPw && <ChangePassword onClose={() => setChangingPw(false)} />}

        <main className="flex-1 max-w-6xl w-full mx-auto px-4 md:px-8 py-8 md:py-10">
          {current === "rooms" && (
            <div className="mb-6 p-4 rounded-xl bg-[#0B192C]/5 border border-[#0B192C]/10 text-sm text-[#0B192C]/70">
              Room rates, availability and reservations are managed exclusively by <b>Little Hotelier</b> and cannot be edited here. Use the eye icon to publish/hide, arrows to reorder, and the bin to delete.
            </div>
          )}

          {current === "home" && <DashboardHome activity={activity} onNav={setCurrent} isAdmin={isAdmin} />}
          {current === "settings" && <Settings />}
          {current === "staff" && <Staff />}
          {current === "corporate" && <Leads collection="corporate-enquiries" label="enquiries" />}
          {current === "orders" && <Leads collection="pre-orders" label="pre-orders" />}
          {["rooms", "menu", "gallery", "offers", "facilities", "awards", "attractions", "certifications", "reviews"].includes(current) && (
            <Manager collection={current} onActivity={pushActivity} />
          )}
        </main>
      </div>
    </div>
  );
}
